<template>
  <el-dialog
    title="用户详情"
    :visible.sync="dialogVisible"
    width="900px"
    :before-close="handleClose"
    class="user-detail-dialog"
    center
    top="5vh"
  >
    <div v-loading="loading" class="dialog-content">
      <el-row :gutter="20" v-if="userDetail">
        <!-- 基本信息 -->
        <el-col :span="24">
          <el-card class="info-card" shadow="never">
            <div slot="header" class="card-header">
              <i class="el-icon-user"></i>
              <span>基本信息</span>
            </div>
            <div class="user-basic-info">
              <div class="avatar-section">
                <el-avatar :size="100" :src="userDetail.avatar" icon="el-icon-user-solid" />
              </div>
              <div class="info-section">
                <el-descriptions :column="2" border>
                  <el-descriptions-item label="用户ID">
                    {{ userDetail.id }}
                  </el-descriptions-item>
                  <el-descriptions-item label="昵称">
                    {{ userDetail.nickname || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="手机号">
                    {{ userDetail.phone || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="性别">
                    <el-tag v-if="userDetail.gender === 1" size="mini">男</el-tag>
                    <el-tag v-else-if="userDetail.gender === 2" type="warning" size="mini">女</el-tag>
                    <span v-else>未知</span>
                  </el-descriptions-item>
                  <el-descriptions-item label="推荐码">
                    <el-tag type="success" size="mini">{{ userDetail.referral_code || '-' }}</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="注册时间">
                    {{ userDetail.created_at | formatDate }}
                  </el-descriptions-item>
                  <el-descriptions-item label="最后登录">
                    {{ userDetail.last_login_time | formatDate }}
                  </el-descriptions-item>
                  <el-descriptions-item label="用户状态">
                    <el-tag :type="userDetail.is_active ? 'success' : 'danger'" size="mini">
                      {{ userDetail.is_active ? '启用' : '禁用' }}
                    </el-tag>
                  </el-descriptions-item>
                </el-descriptions>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 第一行：会员等级 + 门店绑定 -->
        <el-col :span="12">
          <el-card class="info-card equal-height-card" shadow="never">
            <div slot="header" class="card-header">
              <i class="el-icon-medal"></i>
              <span>会员等级</span>
            </div>
            <div class="membership-content">
              <div v-if="userDetail.membership_level" class="membership-info">
                <div class="level-badge" :style="{ background: getMembershipColor(userDetail.membership_level.id) }">
                  <div class="level-name">{{ userDetail.membership_level.level_name }}</div>
                  <div class="level-code">{{ userDetail.membership_level.level_code }}</div>
                </div>
                <el-descriptions :column="1" border class="level-details">
                  <el-descriptions-item label="折扣率">
                    <el-tag type="warning" size="small">{{ formatDiscount(userDetail.membership_level.discount_rate) }}</el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="升级门槛">
                    <div style="font-size: 12px;">
                      <div>消费金额：¥{{ userDetail.membership_level.min_purchase_amount || 0 }}</div>
                      <div>购买次数：{{ userDetail.membership_level.min_purchase_quantity || 0 }}次</div>
                    </div>
                  </el-descriptions-item>
                </el-descriptions>
              </div>
              <div v-else class="no-membership">
                <el-empty description="暂无会员等级信息" :image-size="60" />
              </div>
              
              <!-- 会员等级操作按钮 -->
              <div class="action-btn">
                <el-button type="primary" size="small" @click="handleEditLevel">
                  修改等级
                </el-button>
              </div>
            </div>
          </el-card>
        </el-col>

        <el-col :span="12">
          <el-card class="info-card equal-height-card" shadow="never">
            <div slot="header" class="card-header">
              <i class="el-icon-s-shop"></i>
              <span>门店绑定</span>
            </div>
            <div class="store-content">
              <div v-if="userDetail.bind_store" class="store-info">
                <el-descriptions :column="1" border>
                  <el-descriptions-item label="门店名称">
                    {{ userDetail.bind_store.store_name }}
                  </el-descriptions-item>
                  <el-descriptions-item label="门店ID">
                    {{ userDetail.bind_store.id }}
                  </el-descriptions-item>
                  <el-descriptions-item label="门店类型">
                    <el-tag size="mini">
                      {{ userDetail.bind_store.store_type === 1 ? '门店' : '代理' }}
                    </el-tag>
                  </el-descriptions-item>
                  <el-descriptions-item label="联系人">
                    {{ userDetail.bind_store.contact_person || '-' }}
                  </el-descriptions-item>
                  <el-descriptions-item label="地址">
                    {{ userDetail.bind_store.address || '-' }}
                  </el-descriptions-item>
                </el-descriptions>
              </div>
              <div v-else class="no-store">
                <el-empty description="该用户暂未绑定门店" :image-size="60" />
              </div>
              
              <!-- 门店操作按钮 -->
              <div class="action-btn">
                <template v-if="userDetail.bind_store">
                  <el-button type="warning" size="small" @click="handleChangeStore">
                    更换门店
                  </el-button>
                  <el-button type="danger" size="small" @click="handleUnbindStore">
                    解除绑定
                  </el-button>
                </template>
                <template v-else>
                  <el-button type="primary" size="small" @click="handleBindStore">
                    绑定门店
                  </el-button>
                </template>
              </div>
            </div>
          </el-card>
        </el-col>

        <!-- 第二行：消费统计 + 推荐人信息 -->
        <el-col :span="12">
          <el-card class="info-card" shadow="never">
            <div slot="header" class="card-header">
              <i class="el-icon-s-data"></i>
              <span>消费统计</span>
            </div>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="总消费金额">
                <span class="amount">¥{{ userDetail.total_consumption || 0 }}</span>
              </el-descriptions-item>
              <el-descriptions-item label="总购买件数">
                <span class="quantity">{{ userDetail.total_quantity || 0 }}件</span>
              </el-descriptions-item>
            </el-descriptions>
          </el-card>
        </el-col>

        <!-- 推荐人信息 -->
        <el-col :span="12" v-if="userDetail.referrer">
          <el-card class="info-card" shadow="never">
            <div slot="header" class="card-header">
              <i class="el-icon-share"></i>
              <span>推荐人信息</span>
            </div>
            <el-descriptions :column="1" border>
              <el-descriptions-item label="推荐人ID">
                {{ userDetail.referrer.id }}
              </el-descriptions-item>
              <el-descriptions-item label="推荐人昵称">
                {{ userDetail.referrer.nickname }}
              </el-descriptions-item>
              <el-descriptions-item label="推荐码">
                <el-tag type="success" size="mini">{{ userDetail.referrer.referral_code }}</el-tag>
              </el-descriptions-item>
            </el-descriptions>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">关闭</el-button>
    </span>

    <!-- 门店绑定对话框 -->
    <el-dialog
      :title="bindStoreDialogTitle"
      :visible.sync="bindStoreDialogVisible"
      width="500px"
      append-to-body
    >
      <el-form :model="storeForm" :rules="storeRules" ref="storeForm" label-width="100px">
        <el-form-item label="选择门店" prop="store_id">
          <el-autocomplete
            v-model="storeSearchText"
            :fetch-suggestions="queryStores"
            placeholder="输入门店名称搜索门店，留空则解除绑定"
            :clearable="true"
            value-key="label"
            @select="handleStoreSelect"
            @clear="handleStoreClear"
            style="width: 100%"
          >
            <template slot-scope="{ item }">
              <div class="store-suggestion-item">
                <div class="store-name">{{ item.store_name }}</div>
                <div class="store-info">
                  <span class="store-id">ID: {{ item.id }}</span>
                  <el-tag size="mini" :type="item.store_type === 1 ? 'primary' : 'success'">
                    {{ item.store_type === 1 ? '门店' : '代理' }}
                  </el-tag>
                </div>
              </div>
            </template>
          </el-autocomplete>
          <div v-if="storeForm.store_id" class="selected-store-info">
            <i class="el-icon-success"></i>
            已选择：{{ selectedStoreName }} (ID: {{ storeForm.store_id }})
          </div>
        </el-form-item>
        <el-form-item label="操作原因" prop="reason">
          <el-input
            v-model="storeForm.reason"
            type="textarea"
            rows="3"
            placeholder="请输入操作原因（选填）"
          />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="bindStoreDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitBindStore" :loading="bindStoreLoading">
          确定
        </el-button>
      </span>
    </el-dialog>
  </el-dialog>
</template>

<script>
import { getUserDetail, updateUserBindStore } from '@/api/user'
import { getMembershipLevelColor } from '@/api/user'
import { getStoreList } from '@/api/store'

export default {
  name: 'UserDetailDialog',
  
  props: {
    visible: {
      type: Boolean,
      required: true
    },
    userId: {
      type: Number,
      default: null
    }
  },
  
  filters: {
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      return date.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  },
  
  data() {
    return {
      loading: false,
      userDetail: null,
      
      // 门店绑定对话框
      bindStoreDialogVisible: false,
      bindStoreDialogTitle: '绑定门店',
      bindStoreLoading: false,
      storeForm: {
        store_id: null,
        reason: ''
      },
      storeRules: {
        // store_id 可以为空（解除绑定）
      },
      
      // 门店搜索相关
      storeSearchText: '',
      selectedStoreName: '',
      storeList: [],
      storeListLoading: false
    }
  },
  
  computed: {
    dialogVisible: {
      get() {
        return this.visible
      },
      set(val) {
        this.$emit('update:visible', val)
      }
    }
  },
  
  watch: {
    visible(val) {
      if (val && this.userId) {
        this.loadUserDetail()
      }
    }
  },
  
  methods: {
    async loadUserDetail() {
      if (!this.userId) return
      
      this.loading = true
      try {
        const res = await getUserDetail(this.userId)
        this.userDetail = res.data
      } catch (error) {
        console.error('获取用户详情失败:', error)
        this.$message.error(error.message || '获取用户详情失败')
      } finally {
        this.loading = false
      }
    },
    
    handleEditLevel() {
      this.$emit('edit-level', this.userId)
    },
    
    handleBindStore() {
      this.bindStoreDialogTitle = '绑定门店'
      this.storeForm = {
        store_id: null,
        reason: ''
      }
      this.storeSearchText = ''
      this.selectedStoreName = ''
      this.bindStoreDialogVisible = true
    },
    
    handleChangeStore() {
      this.bindStoreDialogTitle = '更换门店'
      this.storeForm = {
        store_id: this.userDetail.bind_store_id,
        reason: ''
      }
      // 如果已有绑定门店，设置初始值
      if (this.userDetail.bind_store) {
        this.storeSearchText = this.userDetail.bind_store.store_name
        this.selectedStoreName = this.userDetail.bind_store.store_name
      } else {
        this.storeSearchText = ''
        this.selectedStoreName = ''
      }
      this.bindStoreDialogVisible = true
    },
    
    async handleUnbindStore() {
      try {
        await this.$confirm('确定要解除该用户的门店绑定吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        
        this.bindStoreLoading = true
        
        await updateUserBindStore(this.userId, {
          store_id: null,
          reason: '管理员解除门店绑定'
        })
        
        this.$message.success('解除绑定成功')
        this.loadUserDetail()
        this.$emit('refresh')
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('解除绑定失败:', error)
          this.$message.error(error.message || '解除绑定失败')
        }
      } finally {
        this.bindStoreLoading = false
      }
    },
    
    async submitBindStore() {
      try {
        await this.$refs.storeForm.validate()
        
        this.bindStoreLoading = true
        
        await updateUserBindStore(this.userId, {
          store_id: this.storeForm.store_id || null,
          reason: this.storeForm.reason || '管理员操作'
        })
        
        this.$message.success('设置成功')
        this.bindStoreDialogVisible = false
        this.loadUserDetail()
        this.$emit('refresh')
        
      } catch (error) {
        if (error !== 'cancel') {
          console.error('门店绑定失败:', error)
          this.$message.error(error.message || '操作失败')
        }
      } finally {
        this.bindStoreLoading = false
      }
    },
    
    handleClose() {
      this.dialogVisible = false
    },
    
    getMembershipColor(levelId) {
      return getMembershipLevelColor(levelId)
    },
    
    formatDiscount(rate) {
      if (!rate) return '无折扣'
      return `${(rate * 10).toFixed(1)}折`
    },
    
    // 门店搜索相关方法
    async queryStores(queryString, callback) {
      if (!queryString || queryString.length < 1) {
        callback([])
        return
      }
      
      try {
        this.storeListLoading = true
        const response = await getStoreList({
          keyword: queryString,
          page: 1,
          page_size: 20,
          status: 1 // 只查询启用状态的门店
        })
        
        const stores = response.data.list || []
        
        // 为每个门店添加显示用的label
        const suggestions = stores.map(store => ({
          ...store,
          label: `${store.store_name} (ID: ${store.id})`
        }))
        
        callback(suggestions)
      } catch (error) {
        console.error('搜索门店失败:', error)
        callback([])
        this.$message.error('搜索门店失败')
      } finally {
        this.storeListLoading = false
      }
    },
    
    // 选择门店
    handleStoreSelect(store) {
      this.storeForm.store_id = store.id
      this.selectedStoreName = store.store_name
      this.storeSearchText = store.store_name
    },
    
    // 清除门店选择
    handleStoreClear() {
      this.storeForm.store_id = null
      this.selectedStoreName = ''
      this.storeSearchText = ''
    }
  }
}
</script>

<style scoped>
.user-detail-dialog :deep(.el-dialog) {
  border-radius: 16px;
}

.user-detail-dialog :deep(.el-dialog__header) {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  padding: 20px;
  border-radius: 16px 16px 0 0;
}

.user-detail-dialog :deep(.el-dialog__title) {
  color: white;
  font-weight: 600;
}

.user-detail-dialog :deep(.el-dialog__headerbtn .el-dialog__close) {
  color: white;
}

.dialog-content {
  padding: 10px 0;
  min-height: 400px;
}

.info-card {
  margin-bottom: 20px;
  border-radius: 12px;
}

.info-card :deep(.el-card__header) {
  background: #f8f9fa;
  border-bottom: 2px solid #e4e7ed;
  padding: 15px 20px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: #303133;
}

.card-header i {
  font-size: 18px;
  color: #667eea;
}

.user-basic-info {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.avatar-section {
  flex-shrink: 0;
}

.info-section {
  flex: 1;
}

.membership-info {
  flex: 1;
  text-align: center;
  margin-bottom: 15px;
}

.level-badge {
  display: inline-block;
  padding: 20px 40px;
  border-radius: 12px;
  color: white;
  margin-bottom: 20px;
}

.level-name {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 5px;
}

.level-code {
  font-size: 14px;
  opacity: 0.9;
}

.level-details {
  margin-bottom: 15px;
}

.action-btn {
  margin-top: auto; /* 将按钮推到底部 */
  padding-top: 15px;
  text-align: center;
  border-top: 1px solid #f0f2f5;
  background: #fafbfc;
  margin: 0 -20px -20px -20px; /* 延伸到卡片边缘 */
  padding: 15px 20px;
  border-radius: 0 0 12px 12px;
}

.action-btn .el-button {
  margin: 0 5px;
}

/* 等高卡片样式 */
.equal-height-card {
  height: 100%;
}

.equal-height-card :deep(.el-card__body) {
  height: 380px; /* 增加高度让内容更舒适 */
  display: flex;
  flex-direction: column;
}

/* 会员等级内容容器 */
.membership-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.no-membership {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 15px;
}

/* 门店内容容器统一布局 */
.store-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.store-info {
  flex: 1;
  margin-bottom: 15px;
}

.store-info .el-descriptions {
  margin-bottom: 0;
}

.no-store {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  margin-bottom: 15px;
}

.amount {
  font-size: 20px;
  font-weight: 700;
  color: #f56c6c;
}

.quantity {
  font-size: 18px;
  font-weight: 600;
  color: #409eff;
}

:deep(.el-empty__image) {
  width: 80px;
}

:deep(.el-empty__description) {
  margin-top: 10px;
  font-size: 13px;
}

/* 门店搜索相关样式 */
.store-suggestion-item {
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
}

.store-suggestion-item:last-child {
  border-bottom: none;
}

.store-name {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.store-info {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}

.store-id {
  color: #909399;
  font-family: 'Courier New', monospace;
}

.selected-store-info {
  margin-top: 8px;
  padding: 8px 12px;
  background: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 6px;
  font-size: 13px;
  color: #409eff;
  display: flex;
  align-items: center;
  gap: 6px;
}

.selected-store-info i {
  color: #67c23a;
}
</style>

